import React from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/constants";
import { PageSEO } from "@/components/PageSEO";
import { SEO } from "@/lib/seo";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  CheckCircle, XCircle, ArrowRight, FileSpreadsheet, ShieldCheck, MessageCircle, Video,
  FileText, Upload, CalendarCheck, Wallet, Mail, Landmark, Receipt, Calculator,
  PiggyBank, Laptop, BadgeCheck, Euro, CalendarDays
} from "lucide-react";

export const FORFETTARI_LINKS = {
  contabilita: "/pay/9TX6zCxFKIY",
  completo: "/pay/zrErPRhxSxg",
  consulenza197: "/pay/bEi-X9XNCKY",
  consulenza97: "/pay/7NvzR8uDzfg"
};

const C = {
  bg: "bg-[#FAF8F5]",
  bgAlt: "bg-[#F4EFEA]",
  navy: "text-[#0D1527]",
  body: "text-[#4A5568]",
  muted: "text-[#718096]",
  gold: "text-[#B38E46]",
  border: "border-[#E8E2D5]"
};

const goldBtn = "inline-flex items-center justify-center gap-2 bg-[#C5A059] text-white hover:bg-[#B38E46] px-8 py-4 rounded font-bold tracking-wide transition-all hover:scale-[1.02] shadow-[0_4px_14px_rgba(197,160,89,0.35)]";
const outlineBtn = "inline-flex items-center justify-center gap-2 border-2 border-[#C5A059] text-[#B38E46] hover:bg-[#C5A059] hover:text-white px-8 py-4 rounded font-bold tracking-wide transition-all";

const Eyebrow = ({ children }) => (
  <p className="text-[#B38E46] font-bold tracking-[0.3em] text-xs uppercase mb-4">{children}</p>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3 text-[#4A5568]">
    <CheckCircle className="text-[#C5A059] flex-shrink-0 mt-1" size={16} />
    <span>{children}</span>
  </li>
);

const INCLUSO = [
  { icon: Receipt, t: "Registrazione fatture e incassi", d: "Registriamo tutte le fatture emesse e i relativi incassi, per una posizione sempre ordinata." },
  { icon: FileText, t: "Dichiarazione dei redditi", d: "Prepariamo e trasmettiamo la tua dichiarazione dei redditi, con il quadro del regime forfettario." },
  { icon: Calculator, t: "Calcolo dell'imposta sostitutiva", d: "Calcoliamo l'imposta sostitutiva (15%, o 5% per le nuove attività) sui tuoi dati reali." },
  { icon: PiggyBank, t: "Gestione previdenziale", d: "Seguiamo la tua posizione: artigiani e commercianti, gestione separata INPS o cassa professionale." },
  { icon: Landmark, t: "F24 con addebito sull'IBAN", d: "Predisponiamo gli F24 e li addebitiamo direttamente sull'IBAN che indichi all'attivazione." },
  { icon: Mail, t: "Email con esiti e importi", d: "Entro 1 mese dal caricamento dei documenti ricevi l'email con l'esito e il dettaglio dell'F24." }
];

const CONFRONTO = [
  { r: "Registrazione fatture emesse e incassi", a: true, b: true },
  { r: "Dichiarazione dei redditi", a: true, b: true },
  { r: "Calcolo imposta sostitutiva e contributi", a: true, b: true },
  { r: "F24 predisposti e addebitati sull'IBAN indicato", a: true, b: true },
  { r: "Accesso al portale app.casalisilvia.com", a: true, b: true },
  { r: "Email con istruzioni, esiti e dettaglio F24", a: true, b: true },
  { r: "Videochiamata trimestrale di analisi della posizione", a: false, b: true },
  { r: "2 videochiamate straordinarie l'anno per urgenze", a: false, b: true },
  { r: "Risposte continuative via email e WhatsApp", a: false, b: true },
  { r: "Consulenza su un tema specifico", a: "Prenotabile a 97 €", b: "Inclusa nelle videochiamate" },
  { r: "Portale di fatturazione dello studio", a: "Facoltativo · 70 €/anno", b: "Facoltativo · 70 €/anno" }
];

const STEPS = [
  { icon: Wallet, t: "1. Attivi il servizio", d: "Scegli la formula più adatta a te e paghi il primo mese con carta. L'addebito si rinnova automaticamente ogni mese." },
  { icon: FileText, t: "2. Compili il modulo", d: "Dopo il pagamento inserisci tutti i dati: attività, posizione previdenziale e IBAN per l'addebito degli F24." },
  { icon: Mail, t: "3. Ricevi le istruzioni", d: "Ti arriva una email con tutte le istruzioni operative e le credenziali del portale app.casalisilvia.com." },
  { icon: Upload, t: "4. Carichi i documenti", d: "Gli XML delle fatture emesse quando vuoi (nulla, se attivi il nostro portale di fatturazione). Entro gennaio: date di incasso e contributi previdenziali versati." },
  { icon: CalendarCheck, t: "5. Dichiarazione e F24", d: "Prepariamo la dichiarazione dei redditi e gli F24. Entro 1 mese dal caricamento ricevi l'email con l'esito e tutti gli importi." }
];

const TIMELINE = [
  { m: "TUTTO L'ANNO", t: "Emetti e carichi", d: "Fatturi con il portale che preferisci e carichi gli XML quando vuoi. Con il nostro portale, non carichi nulla." },
  { m: "GENNAIO", t: "Incassi e contributi", d: "Carichi le date di incasso delle fatture e i contributi previdenziali versati l'anno precedente: gli F24 se hai l'INPS, il documento della tua cassa privata con il totale versato." },
  { m: "ENTRO 1 MESE", t: "Esito e dettaglio F24", d: "Completato il caricamento, ricevi l'email con l'esito della dichiarazione e il dettaglio di ogni importo." },
  { m: "GIUGNO · LUGLIO", t: "Saldo e primo acconto", d: "Gli F24 di saldo e primo acconto vengono addebitati direttamente sull'IBAN che hai indicato." },
  { m: "NOVEMBRE", t: "Secondo acconto", d: "Stesso meccanismo: F24 predisposto da noi e addebitato sull'IBAN indicato. Nessuna scadenza da ricordare." }
];

const ADATTO = [
  "Hai una partita IVA in regime forfettario, o stai per aprirla",
  "Vuoi avere sempre un Dottore Commercialista con cui parlare: con noi non sei mai lasciato solo",
  "Vuoi la tutela dell'iscrizione all'Albo: esame di Stato, formazione specifica accertata e aggiornamento continuo obbligatorio — requisiti che una piattaforma online non è tenuta ad avere",
  "Vuoi la garanzia di un'assicurazione professionale a copertura dell'attività svolta",
  "Preferisci gestire tutto online, senza vincoli di orario",
  "Vuoi un costo mensile costante, conosciuto in anticipo"
];

const NON_ADATTO = [
  "Sei in regime ordinario o semplificato, o hai una società (SRL, SNC…): in questi casi lo studio ti segue con un preventivo dedicato",
  "Preferisci consegnare i documenti cartacei di persona"
];

const RASSICURAZIONI = [
  { icon: Laptop, t: "Tutto online", d: "Attivazione, documenti e comunicazioni: tutto dal portale e via email, da qualsiasi parte d'Italia." },
  { icon: ShieldCheck, t: "Rispondiamo noi", d: "Delle annualità che gestiamo rispondiamo noi, come previsto dal nostro incarico professionale." },
  { icon: BadgeCheck, t: "Iscritti all'Albo", d: "Dottori Commercialisti iscritti all'ODCEC, con formazione continua obbligatoria e assicurazione professionale." },
  { icon: Euro, t: "Costi chiari", d: "43 € o 96 € al mese. L'unico costo aggiuntivo possibile è il portale di fatturazione facoltativo: 70 €/anno IVA inclusa." }
];

const FAQS = [
  { q: "Ho già un commercialista: come funziona il passaggio?", a: "Il passaggio lo gestisci direttamente tu, ed è semplice: chiedi al tuo precedente professionista i documenti dell'ultimo anno (dichiarazione dei redditi presentata, F24 versati) e li carichi nel portale. Nel modulo di attivazione e nell'email di istruzioni ti indichiamo esattamente che cosa serve." },
  { q: "Devo ancora aprire la partita IVA: mi seguite?", a: "Sì. Se hai dubbi sulla convenienza del regime o sull'inquadramento, puoi partire dalla consulenza personalizzata (197 €) o dagli strumenti di simulazione dello studio; una volta aperta la posizione, attivi il servizio di contabilità." },
  { q: "Che cosa devo fare io, concretamente, durante l'anno?", a: "Emetti le fatture con il portale che preferisci e carichi gli XML su app.casalisilvia.com quando vuoi (se attivi il nostro portale, nemmeno quello). A gennaio carichi le date di incasso e i contributi previdenziali versati l'anno precedente. Al resto pensiamo noi." },
  { q: "Come vengono pagati gli F24?", a: "Con addebito diretto sull'IBAN che indichi nel modulo di attivazione. Prima di ogni scadenza ricevi l'email con l'esito e il dettaglio degli importi. Per cambiare IBAN è sufficiente inviare una email allo studio." },
  { q: "Il portale di fatturazione a 70 €/anno è obbligatorio?", a: "No, è facoltativo: puoi continuare a fatturare con il portale che già usi. Se scegli il nostro (70 €/anno IVA inclusa) non devi caricare gli XML: le fatture le vediamo direttamente. Il canone viene addebitato all'attivazione e poi ogni 1° gennaio; a metà dicembre ricevi un'email di preavviso e puoi disattivarlo rispondendo entro il 31 dicembre." },
  { q: "Chi calcola i contributi INPS o della cassa professionale?", a: "Noi, in base alla tua gestione: artigiani, commercianti, gestione separata INPS o cassa professionale. A gennaio carichi gli F24 versati l'anno precedente (se hai l'INPS) oppure il documento della tua area riservata con il totale versato (se hai una cassa privata)." },
  { q: "Cosa succede se supero i limiti del regime forfettario?", a: "Le condizioni indicate in questa pagina valgono finché resti nel regime forfettario. Se esci dal regime, la contabilità diventa più articolata: ti prepariamo un preventivo dedicato e decidi liberamente se proseguire con noi." },
  { q: "Come funziona il rinnovo? Posso disdire?", a: "L'addebito è mensile e automatico sulla carta. Al 31/12 l'incarico si rinnova per periodi di due anni in due anni, salvo disdetta da comunicare via PEC almeno 3 mesi prima della scadenza." },
  { q: "E se ricevo un avviso dall'Agenzia delle Entrate?", a: "Per le annualità gestite da noi rispondiamo noi. Per questioni relative ad annualità precedenti o temi specifici (avvisi di irregolarità, accertamenti) i clienti del servizio possono prenotare la consulenza specifica a 97 €." },
  { q: "Devo venire in studio?", a: "No: attivazione, caricamento dei documenti e comunicazioni avvengono online, e le consulenze si svolgono in videochiamata. Lo studio è a Ravenna e segue clienti in tutta Italia." },
  { q: "Posso continuare a usare il mio programma di fatturazione?", a: "Sì: fatturi con lo strumento che preferisci e carichi gli XML delle fatture emesse su app.casalisilvia.com. In alternativa puoi usare il nostro portale a 70 €/anno IVA inclusa e non caricare nulla." }
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQS.map(f => ({
    "@type": "Question",
    "name": f.q,
    "acceptedAnswer": { "@type": "Answer", "text": f.a }
  }))
};

const CellCheck = ({ v }) => {
  if (v === true) return <CheckCircle className="text-[#2D6A4F] mx-auto" size={18} />;
  if (v === false) return <span className="text-[#B8B2A6] block text-center">—</span>;
  return <span className="text-[#4A5568] text-xs block text-center leading-snug">{v}</span>;
};

export default function ForfettariPage() {
  const [openFaq, setOpenFaq] = React.useState("");
  const [qmdpVendita, setQmdpVendita] = React.useState(false);

  React.useEffect(() => {
    axios.get(`${API}/qmdp-stato`).then(r => setQmdpVendita(!!r.data.vendita_attiva)).catch(() => {});
  }, []);

  const apriFaqPassaggio = (e) => {
    e.preventDefault();
    setOpenFaq("faq-0");
    document.getElementById("faq")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main data-testid="forfettari-page" className={`${C.bg} pt-28`}>
      <PageSEO {...SEO.forfettari} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* backdrop rimosso: header ora chiaro su tutto il sito */}

      {/* Hero */}
      <section className="py-16 sm:py-20 text-center max-w-4xl mx-auto px-6" data-testid="forfettari-hero">
        <span className="inline-flex items-center gap-2 bg-[#F7F0E1] border border-[#E2D1B0] text-[#8A6D33] text-xs font-bold tracking-[0.2em] px-4 py-2 rounded-full mb-6">
          <BadgeCheck size={14} /> PER PARTITE IVA IN REGIME FORFETTARIO
        </span>
        <h1 className={`font-heading text-4xl sm:text-5xl lg:text-6xl ${C.navy} leading-tight mb-6`}>
          LA TUA CONTABILITÀ, GESTITA DA<br /><span className="text-[#B38E46]">DOTTORI COMMERCIALISTI</span>
        </h1>
        <p className={`text-xl ${C.body} leading-relaxed mb-4`}>
          Registrazione delle fatture e degli incassi, dichiarazione dei redditi e F24 addebitati
          direttamente sul tuo conto. A <span className="text-[#B38E46] font-semibold">43 € al mese</span>, tutto incluso.
        </p>
        <p className={`${C.muted} max-w-2xl mx-auto mb-8`}>
          La differenza rispetto alle piattaforme automatiche: qui la tua posizione è seguita da Dottori
          Commercialisti iscritti all'Albo, e delle annualità che gestiamo rispondiamo noi.
        </p>

        <div
          className="bg-[#FAF6EE] border-2 border-[#C5A059] rounded-lg p-5 sm:p-7 text-left mb-8 shadow-[0_8px_30px_rgba(197,160,89,0.15)]"
          data-testid="forfettari-banner-31dicembre"
        >
          <h2 className={`font-heading text-base md:text-lg ${C.navy} leading-snug mb-3`}>
            ATTIVA ENTRO IL 31 DICEMBRE: DAL 1° GENNAIO CI PENSIAMO NOI
          </h2>
          <p className={`${C.body} text-sm sm:text-base leading-relaxed`}>
            Dichiarazione dei redditi, imposta sostitutiva, contributi INPS e F24: tutto online,
            senza appuntamenti. Hai già un commercialista?{" "}
            <a
              href="#faq"
              onClick={apriFaqPassaggio}
              className="text-[#B38E46] underline font-semibold hover:text-[#8A6D33] transition-colors"
              data-testid="banner-link-passaggio"
            >
              Ti spieghiamo come passare da noi
            </a>.
          </p>
          <a
            href={FORFETTARI_LINKS.contabilita}
            className={`${goldBtn} w-full sm:w-auto mt-5 text-sm sm:text-base px-6 sm:px-8`}
            data-testid="banner-cta-inizia-ora"
          >
            INIZIA ORA — 43 €/MESE <ArrowRight size={18} />
          </a>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="#piani" className={goldBtn} data-testid="hero-cta-piani">SCOPRI I PIANI <ArrowRight size={18} /></a>
          <a href="#faq" className={outlineBtn} data-testid="hero-cta-faq">LEGGI LE DOMANDE FREQUENTI</a>
        </div>
      </section>

      {/* Cosa è incluso */}
      <section className={`py-16 sm:py-20 ${C.bgAlt} border-y ${C.border}`} data-testid="forfettari-incluso">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <Eyebrow>TRASPARENZA SUGLI ADEMPIMENTI</Eyebrow>
            <h2 className={`font-heading text-3xl sm:text-4xl ${C.navy} mb-3`}>COSA È INCLUSO, NEL DETTAGLIO</h2>
            <p className={`${C.body} max-w-2xl mx-auto`}>Tutto quello che serve alla gestione ordinaria di una partita IVA in regime forfettario, senza voci a sorpresa.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {INCLUSO.map((x, i) => (
              <div key={i} className={`bg-white border ${C.border} rounded-lg p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300`}>
                <x.icon className="text-[#C5A059] mb-4" size={26} />
                <h3 className={`font-heading text-xl ${C.navy} mb-2`}>{x.t}</h3>
                <p className={`${C.body} text-sm leading-relaxed`}>{x.d}</p>
              </div>
            ))}
          </div>

          {/* Confronto piani */}
          <div className="mt-16" data-testid="forfettari-confronto">
            <h3 className={`font-heading text-2xl sm:text-3xl ${C.navy} text-center mb-8`}>I DUE PIANI A CONFRONTO</h3>
            <p className={`sm:hidden text-center ${C.muted} text-xs mb-3`}>Scorri la tabella lateralmente per confrontare i piani →</p>
            <div className={`bg-white border ${C.border} rounded-lg overflow-hidden overflow-x-auto shadow-sm`}>
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="bg-[#101B2B]">
                    <th className="text-left px-5 py-4 text-white/80 font-medium w-1/2">Cosa comprende</th>
                    <th className="px-4 py-4 text-white font-heading text-base">CONTABILITÀ<br /><span className="text-[#C5A059] text-sm">43 €/mese</span><br /><span className="text-white/50 text-xs font-sans font-normal">IVA inclusa</span></th>
                    <th className="px-4 py-4 text-white font-heading text-base">CONTABILITÀ + CONSULENZA<br /><span className="text-[#C5A059] text-sm">96 €/mese</span><br /><span className="text-white/50 text-xs font-sans font-normal">IVA inclusa</span></th>
                  </tr>
                </thead>
                <tbody>
                  {CONFRONTO.map((row, i) => (
                    <tr key={i} className={`border-t ${C.border} ${i % 2 ? "bg-[#FAF8F5]" : "bg-white"}`}>
                      <td className={`px-5 py-3.5 ${C.body}`}>{row.r}</td>
                      <td className="px-4 py-3.5"><CellCheck v={row.a} /></td>
                      <td className="px-4 py-3.5"><CellCheck v={row.b} /></td>
                    </tr>
                  ))}
                  <tr className={`border-t ${C.border} bg-[#FAF6EE]`}>
                    <td className={`px-5 py-4 font-semibold ${C.navy}`}>Attivazione</td>
                    <td className="px-4 py-4 text-center">
                      <a href={FORFETTARI_LINKS.contabilita} className="inline-block border-2 border-[#C5A059] text-[#B38E46] hover:bg-[#C5A059] hover:text-white px-4 py-2 rounded font-bold text-xs tracking-wide transition-all" data-testid="confronto-cta-43">ATTIVA A 43 €</a>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <a href={FORFETTARI_LINKS.completo} className="inline-block bg-[#C5A059] text-white hover:bg-[#B38E46] px-4 py-2 rounded font-bold text-xs tracking-wide transition-all" data-testid="confronto-cta-96">ATTIVA A 96 €</a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Come funziona */}
      <section className="py-16 sm:py-20" data-testid="forfettari-come-funziona">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <Eyebrow>IL PERCORSO</Eyebrow>
            <h2 className={`font-heading text-3xl sm:text-4xl ${C.navy}`}>COME FUNZIONA</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {STEPS.map((s, i) => (
              <div key={i} className={`bg-white border ${C.border} rounded-lg p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300`}>
                <s.icon className="text-[#C5A059] mb-4" size={24} />
                <h3 className={`font-heading text-lg ${C.navy} mb-2`}>{s.t}</h3>
                <p className={`${C.body} text-sm leading-relaxed`}>{s.d}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={FORFETTARI_LINKS.contabilita} className={`${outlineBtn} text-lg px-10 py-5`} data-testid="forfettari-attiva-contabilita">
                CONTABILITÀ — 43 €/MESE
              </a>
              <a href={FORFETTARI_LINKS.completo} className={`${goldBtn} text-lg px-10 py-5`} data-testid="forfettari-attiva-completo">
                CONTABILITÀ + CONSULENZA — 96 €/MESE <ArrowRight size={20} />
              </a>
            </div>
            <p className={`${C.muted} text-xs mt-4`}>Addebito mensile automatico sulla carta. Al 31/12 l'incarico si rinnova per periodi di due anni in due anni, salvo disdetta da comunicare via PEC almeno 3 mesi prima della scadenza.</p>
          </div>
        </div>
      </section>

      {/* Anno fiscale */}
      <section className={`py-16 sm:py-20 ${C.bgAlt} border-y ${C.border}`} data-testid="forfettari-timeline">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <Eyebrow>SAI SEMPRE COSA ASPETTARTI</Eyebrow>
            <h2 className={`font-heading text-3xl sm:text-4xl ${C.navy}`}>IL TUO ANNO FISCALE CON NOI</h2>
          </div>
          <div className="relative pl-8 sm:pl-10">
            <div className="absolute left-[13px] sm:left-[17px] top-2 bottom-2 w-px bg-[#D6CFC0]" aria-hidden="true" />
            {TIMELINE.map((x, i) => (
              <div key={i} className="relative pb-10 last:pb-0">
                <span className="absolute -left-8 sm:-left-10 top-1 w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-white border-2 border-[#C5A059] flex items-center justify-center">
                  <CalendarDays size={14} className="text-[#B38E46]" />
                </span>
                <p className="text-[#B38E46] font-bold tracking-[0.2em] text-xs mb-1">{x.m}</p>
                <h3 className={`font-heading text-xl ${C.navy} mb-1.5`}>{x.t}</h3>
                <p className={`${C.body} text-sm leading-relaxed max-w-2xl`}>{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Piani */}
      <section id="piani" className="py-16 sm:py-20 scroll-mt-28" data-testid="forfettari-piani">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <Eyebrow>DUE FORMULE MENSILI</Eyebrow>
            <h2 className={`font-heading text-3xl sm:text-4xl ${C.navy} mb-3`}>SCEGLI QUANTO VUOI ESSERE SEGUITO</h2>
            <p className={C.body}>Un unico interlocutore in entrambi i casi: il tuo commercialista.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className={`bg-white border ${C.border} rounded-lg p-8 shadow-sm`} data-testid="piano-contabilita">
              <h3 className={`font-heading text-2xl ${C.navy} mb-1`}>CONTABILITÀ</h3>
              <p className="text-4xl font-heading text-[#B38E46] mb-6">43 €<span className={`text-base ${C.muted}`}> /mese</span><span className={`block text-sm ${C.muted} font-sans mt-1`}>IVA inclusa</span></p>
              <ul className="space-y-3 text-sm mb-6">
                <Bullet>Registrazione delle fatture emesse e degli incassi</Bullet>
                <Bullet>Dichiarazione dei redditi</Bullet>
                <Bullet>Calcolo degli F24 e addebito diretto sull'IBAN che indichi</Bullet>
                <Bullet>Accesso al portale app.casalisilvia.com</Bullet>
                <Bullet>Fatturi con il portale che preferisci; in alternativa il nostro a 70 €/anno IVA inclusa (addebitato all'attivazione e poi ogni 1° gennaio) — e in quel caso non carichi gli XML: le fatture le vediamo direttamente</Bullet>
                <Bullet>Quando ti serve un confronto: consulenza su un tema specifico prenotabile a 97 €</Bullet>
              </ul>
              <a href={FORFETTARI_LINKS.contabilita} className="block text-center border-2 border-[#C5A059] text-[#B38E46] hover:bg-[#C5A059] hover:text-white px-6 py-3 rounded font-bold tracking-wide transition-all" data-testid="btn-piano-contabilita">ATTIVA A 43 €/MESE</a>
            </div>
            <div className="bg-[#FAF6EE] border-2 border-[#C5A059] rounded-lg p-8 relative shadow-[0_8px_30px_rgba(197,160,89,0.15)]" data-testid="piano-completo">
              <span className="absolute -top-3 left-8 bg-[#C5A059] text-white text-xs font-bold px-3 py-1 rounded">CON CONSULENZA CONTINUATIVA</span>
              <h3 className={`font-heading text-2xl ${C.navy} mb-1`}>CONTABILITÀ + CONSULENZA</h3>
              <p className="text-4xl font-heading text-[#B38E46] mb-6">96 €<span className={`text-base ${C.muted}`}> /mese</span><span className={`block text-sm ${C.muted} font-sans mt-1`}>IVA inclusa</span></p>
              <ul className="space-y-3 text-sm mb-6">
                <Bullet>Tutto quello che è incluso nella Contabilità</Bullet>
                <Bullet>Una videochiamata a trimestre: analisi della posizione, previsione e calcolo delle imposte, risposte a ogni domanda</Bullet>
                <Bullet>2 videochiamate straordinarie l'anno per urgenze specifiche</Bullet>
                <Bullet>Risposte continuative via email e WhatsApp</Bullet>
              </ul>
              <a href={FORFETTARI_LINKS.completo} className="block text-center bg-[#C5A059] text-white hover:bg-[#B38E46] px-6 py-3 rounded font-bold tracking-wide transition-all hover:scale-[1.02]" data-testid="btn-piano-completo">ATTIVA A 96 €/MESE</a>
            </div>
          </div>
          <p className={`text-center ${C.muted} text-sm mt-8`}>
            Le condizioni indicate valgono per la permanenza nel regime forfettario. In caso di passaggio ad altro regime, prepariamo un preventivo dedicato.
          </p>
        </div>
      </section>

      {/* È adatto a te? */}
      <section className={`py-16 sm:py-20 ${C.bgAlt} border-y ${C.border}`} data-testid="forfettari-adatto">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <Eyebrow>PRIMA DI ATTIVARE</Eyebrow>
            <h2 className={`font-heading text-3xl sm:text-4xl ${C.navy}`}>È ADATTO A TE?</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white border border-[#2D6A4F]/25 rounded-lg p-8" data-testid="adatto-si">
              <h3 className="font-heading text-2xl text-[#2D6A4F] mb-5">È ADATTO SE…</h3>
              <ul className="space-y-4 text-sm">
                {ADATTO.map((x, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#4A5568]">
                    <CheckCircle className="text-[#2D6A4F] flex-shrink-0 mt-0.5" size={17} /><span>{x}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className={`bg-white border ${C.border} rounded-lg p-8`} data-testid="adatto-no">
              <h3 className={`font-heading text-2xl ${C.navy} mb-5`}>NON È ADATTO SE…</h3>
              <ul className="space-y-4 text-sm">
                {NON_ADATTO.map((x, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#4A5568]">
                    <XCircle className="text-[#B8B2A6] flex-shrink-0 mt-0.5" size={17} /><span>{x}</span>
                  </li>
                ))}
              </ul>
              <p className={`${C.muted} text-xs mt-6`}>
                Regime ordinario o società? <Link to="/contatti" className="text-[#B38E46] underline" data-testid="link-contatti-ordinario">Contattaci</Link>: lo studio segue anche queste posizioni, con preventivo su misura.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Rassicurazioni */}
      <section className="py-16 sm:py-20" data-testid="forfettari-rassicurazioni">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <Eyebrow>PERCHÉ PUOI STARE TRANQUILLO</Eyebrow>
            <h2 className={`font-heading text-3xl sm:text-4xl ${C.navy}`}>NON UNA PIATTAFORMA: UNO STUDIO</h2>
            <p className={`${C.body} max-w-3xl mx-auto mt-4`}>
              Il servizio è erogato da SC Dottori Commercialisti S.r.l. STP: professionisti iscritti all'Ordine dei
              Dottori Commercialisti e degli Esperti Contabili, soggetti a deontologia, formazione continua e
              assicurazione professionale.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {RASSICURAZIONI.map((x, i) => (
              <div key={i} className={`bg-white border ${C.border} rounded-lg p-6 text-center hover:-translate-y-1 hover:shadow-lg transition-all duration-300`}>
                <x.icon className="text-[#C5A059] mx-auto mb-4" size={28} />
                <h3 className={`font-heading text-lg ${C.navy} mb-2`}>{x.t}</h3>
                <p className={`${C.body} text-sm leading-relaxed`}>{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consulenze */}
      <section className={`py-16 sm:py-20 ${C.bgAlt} border-y ${C.border}`} data-testid="forfettari-consulenze">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <Eyebrow>QUANDO SERVE UN CONFRONTO DIRETTO</Eyebrow>
            <h2 className={`font-heading text-3xl sm:text-4xl ${C.navy}`}>LE CONSULENZE IN VIDEOCHIAMATA</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className={`bg-white border ${C.border} rounded-lg p-8`}>
              <Video className="text-[#C5A059] mb-4" size={26} />
              <h3 className={`font-heading text-2xl ${C.navy} mb-2`}>CONSULENZA PERSONALIZZATA — 197 €</h3>
              <p className="text-[#8A6D33] text-xs font-bold tracking-wide mb-3">APERTA A TUTTI, ANCHE SE NON SEI CLIENTE DELLO STUDIO</p>
              <p className={`${C.body} text-sm leading-relaxed mb-6`}>
                Gli strumenti dello studio ti danno i numeri; con la consulenza gli stessi calcoli li faccio io sulla tua
                posizione reale, te li spiego in videochiamata e ricevi un report scritto da conservare.
              </p>
              <a href={FORFETTARI_LINKS.consulenza197} className="inline-flex items-center gap-2 bg-[#C5A059] text-white hover:bg-[#B38E46] px-6 py-3 rounded font-bold text-sm tracking-wide transition-all" data-testid="btn-consulenza-197">PRENOTA LA CONSULENZA <ArrowRight size={16} /></a>
            </div>
            <div className={`bg-white border ${C.border} rounded-lg p-8`}>
              <MessageCircle className="text-[#C5A059] mb-4" size={26} />
              <h3 className={`font-heading text-2xl ${C.navy} mb-2`}>CONSULENZA SPECIFICA — 97 €</h3>
              <p className="text-[#8A6D33] text-xs font-bold tracking-wide mb-3">RISERVATA A CHI HA ATTIVATO LA CONTABILITÀ A 43 €/MESE</p>
              <p className={`${C.body} text-sm leading-relaxed mb-6`}>
                Se sei già cliente del servizio di contabilità, in ogni momento puoi prenotare una videochiamata su un
                tema puntuale: avvisi di irregolarità, accertamenti, consulenza fiscale, passaggio di regime,
                modalità di fatturazione e altro.
              </p>
              <a href={FORFETTARI_LINKS.consulenza97} className="inline-flex items-center gap-2 border-2 border-[#C5A059] text-[#B38E46] hover:bg-[#C5A059] hover:text-white px-6 py-3 rounded font-bold text-sm tracking-wide transition-all" data-testid="btn-consulenza-97">RICHIEDI A 97 € <ArrowRight size={16} /></a>
            </div>
          </div>
        </div>
      </section>

      {/* Strumenti */}
      <section className="py-16 sm:py-20" data-testid="forfettari-strumenti">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FileSpreadsheet className="text-[#C5A059] mx-auto mb-4" size={30} />
          <h2 className={`font-heading text-3xl sm:text-4xl ${C.navy} mb-3`}>GLI STRUMENTI PER I FORFETTARI</h2>
          <p className={`${C.body} mb-8`}>Excel e calcolatori creati dallo studio: gestione del forfettario, simulazione di apertura partita IVA, convenienza dei regimi, prezzi e rimborsi.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/strumenti" className={outlineBtn} data-testid="btn-strumenti">VAI AGLI STRUMENTI</Link>
            <a href={FORFETTARI_LINKS.consulenza197} className={goldBtn}>PREFERISCI CHE LI CALCOLI IO? — 197 €</a>
          </div>
        </div>
      </section>

      {/* Quanto Mettere da Parte — visibile solo a vendita accesa */}
      {qmdpVendita && (
        <section className="py-10" data-testid="forfettari-qmdp">
          <div className="max-w-4xl mx-auto px-6">
            <div className="bg-white border-2 border-[#3E8ED0] rounded-lg p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5">
              <CalendarCheck className="text-[#2F7DBF] shrink-0" size={32} />
              <div className="flex-1">
                <p className="text-[#64708A] text-[11px] font-bold tracking-[0.2em]">SERVIZIO MENSILE · 9,90 € AL MESE</p>
                <h3 className={`font-heading text-2xl ${C.navy} mt-1`}>Quanto Mettere da Parte — Forfettari</h3>
                <p className={`${C.body} text-sm mt-1`}>
                  Gestisci tu gli adempimenti? Ogni mese ci dici quanto hai incassato e ti diciamo quanto tenere da
                  parte per tasse e contributi, con le date dei pagamenti.
                </p>
              </div>
              <Link to="/strumenti/quanto-mettere-da-parte" data-testid="forfettari-qmdp-link"
                className="shrink-0 bg-[#2F7DBF] text-white hover:bg-[#1F4E75] px-6 py-3 rounded font-bold text-sm tracking-wide transition-colors text-center">
                SCOPRI
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section id="faq" className={`py-16 sm:py-20 ${C.bgAlt} border-t ${C.border} scroll-mt-28`} data-testid="forfettari-faq">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <Eyebrow>OGNI DUBBIO, UNA RISPOSTA</Eyebrow>
            <h2 className={`font-heading text-3xl sm:text-4xl ${C.navy}`}>DOMANDE FREQUENTI</h2>
          </div>
          <Accordion type="single" collapsible value={openFaq} onValueChange={setOpenFaq} className="space-y-3">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className={`bg-white border ${C.border} rounded-lg px-6 border-b`}>
                <AccordionTrigger className={`text-left font-medium ${C.navy} hover:no-underline hover:text-[#B38E46] py-5 text-[15px]`} data-testid={`faq-trigger-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className={`${C.body} text-sm leading-relaxed pb-5`}>
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className={`text-center ${C.muted} text-sm mt-8`}>
            Non trovi la tua domanda? <Link to="/contatti" className="text-[#B38E46] underline" data-testid="faq-link-contatti">Scrivici</Link>: rispondiamo noi.
          </p>
        </div>
      </section>

      {/* CTA finale */}
      <section className="py-16 sm:py-20 bg-[#101B2B]" data-testid="forfettari-cta-finale">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-4">LA CONTABILITÀ È IL NOSTRO LAVORO.<br />TU PENSA AL TUO.</h2>
          <p className="text-white/60 mb-8">Attivi oggi, compili il modulo e da domani la tua posizione è seguita dallo studio.</p>
          <a href={FORFETTARI_LINKS.contabilita} className={`${goldBtn} text-lg px-10 py-5`} data-testid="forfettari-cta-finale-btn">
            INIZIA ORA — 43 €/MESE <ArrowRight size={20} />
          </a>
          <p className="text-white/40 text-xs mt-5">Hai già pagato? <Link to="/forfettari/benvenuto" className="text-[#C5A059] underline" data-testid="link-onboarding">Compila il modulo di attivazione</Link>.</p>
        </div>
      </section>
    </main>
  );
}
